import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, Zap, Shield, Palette, 
  Bell, ArrowRight, CheckCircle, Calendar, 
  Upload, Mail, QrCode, Lock, Globe, Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';

const updates = [
  {
    date: 'Mars 2026',
    title: 'Transfert P2P (Ultimate)',
    description: 'Envoyez vos fichiers directement de navigateur a navigateur grace a WebRTC. Aucune limite de taille, aucun stockage serveur. Vos donnees ne transitent jamais par nos serveurs.',
    icon: Zap,
    features: ['WebRTC direct', 'Aucune limite de taille', 'Sans stockage serveur', 'QR code & lien de partage'],
    type: 'new'
  },
  {
    date: 'Février 2026',
    title: 'Application Mobile PWA',
    description: 'Installez SENDBOX directement sur votre smartphone comme une application native. Aucun téléchargement depuis les stores requis.',
    icon: Smartphone,
    features: ['Installation en un clic', 'Mode hors connexion', 'Notifications push', 'Interface optimisée mobile'],
    type: 'new'
  },
  {
    date: 'Février 2026',
    title: 'Extensions Email',
    description: 'Envoyez des fichiers volumineux directement depuis Outlook et Gmail grâce à nos extensions officielles.',
    icon: Mail,
    features: ['Extension Outlook', 'Extension Gmail', 'Intégration transparente', 'Fonctionnalités Pro'],
    type: 'new'
  },
  {
    date: 'Janvier 2026',
    title: 'Templates Email Personnalisables',
    description: 'Les administrateurs peuvent maintenant personnaliser tous les emails envoyés par la plateforme : sujet, contenu, logo et couleurs.',
    icon: Palette,
    features: ['6 templates personnalisables', 'Éditeur visuel', 'Variables dynamiques', 'Prévisualisation en temps réel'],
    type: 'improvement'
  },
  {
    date: 'Janvier 2026',
    title: 'Thèmes Saisonniers',
    description: 'Les administrateurs peuvent configurer des thèmes visuels saisonniers qui s\'affichent automatiquement sur la page d\'accueil.',
    icon: Sparkles,
    features: ['Fonds personnalisés', 'Programmation automatique', 'Gestion par période', 'Activation/désactivation facile'],
    type: 'new'
  },
  {
    date: 'Décembre 2025',
    title: 'Système Anti-Abus',
    description: 'Protection automatique contre les abus avec journalisation IP, limites de téléchargement et gestion des blocages.',
    icon: Shield,
    features: ['Détection automatique', 'Alertes admin', 'Blocage IP', 'Liste blanche'],
    type: 'security'
  },
  {
    date: 'Décembre 2025',
    title: 'Messagerie Support',
    description: 'Nouveau système de support conversationnel permettant aux utilisateurs de suivre leurs demandes et d\'échanger avec le support.',
    icon: Bell,
    features: ['Conversations persistantes', 'Notifications', 'Historique complet', 'Réponses admin'],
    type: 'new'
  },
  {
    date: 'Novembre 2025',
    title: 'Chiffrement de Bout en Bout',
    description: 'Vos fichiers sont maintenant chiffrés côté client avant l\'envoi. Seul le destinataire peut les déchiffrer.',
    icon: Lock,
    features: ['Chiffrement AES-256', 'Clé dans l\'URL', 'Aucun accès serveur', 'Sécurité maximale'],
    type: 'security'
  },
  {
    date: 'Novembre 2025',
    title: 'QR Code de Partage',
    description: 'Chaque transfert génère automatiquement un QR code scannable pour un partage encore plus rapide.',
    icon: QrCode,
    features: ['Génération automatique', 'Téléchargeable', 'Scannable mobile', 'Intégré au partage'],
    type: 'new'
  }
];

const NewsPage = () => {
  const typeColors = {
    new: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    improvement: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    security: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  const typeLabels = {
    new: 'Nouveauté',
    improvement: 'Amélioration',
    security: 'Sécurité'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <SEOHead 
        title="Nouveautés" 
        description="Découvrez les dernières nouveautés et mises à jour de SENDBOX : nouvelles fonctionnalités, améliorations de sécurité et optimisations." 
      />
      <NavigationMenu />
      
      <main className="pt-24 pb-32">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto px-6 text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm mb-6">
            <Sparkles className="w-4 h-4" />
            Quoi de neuf ?
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Nouveautés<br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              SENDBOX
            </span>
          </h1>
          <p className="text-lg md:text-xl text-blue-200/70 max-w-3xl mx-auto">
            Découvrez les dernières fonctionnalités et améliorations 
            que nous avons ajoutées à SENDBOX.
          </p>
        </section>

        {/* Updates Timeline */}
        <section className="max-w-4xl mx-auto px-6">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-500/50 via-blue-500/30 to-transparent hidden md:block"></div>

            <div className="space-y-8">
              {updates.map((update, index) => {
                const Icon = update.icon;
                return (
                  <div key={index} className="relative flex gap-6">
                    {/* Timeline dot */}
                    <div className="hidden md:flex flex-shrink-0 w-16 h-16 bg-slate-800 border-2 border-cyan-500/50 rounded-2xl items-center justify-center z-10">
                      <Icon className="w-7 h-7 text-cyan-400" />
                    </div>

                    {/* Content Card */}
                    <div className="flex-1 bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 hover:border-blue-500/30 transition-all">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="flex items-center gap-1.5 text-blue-300/60 text-sm">
                          <Calendar className="w-4 h-4" />
                          {update.date}
                        </span>
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${typeColors[update.type]}`}>
                          {typeLabels[update.type]}
                        </span>
                      </div>
                      
                      <div className="md:hidden flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                          <Icon className="w-5 h-5 text-blue-400" />
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-white mb-2">{update.title}</h3>
                      <p className="text-blue-200/70 mb-4">{update.description}</p>
                      
                      <div className="flex flex-wrap gap-2">
                        {update.features.map((feature, idx) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center gap-1.5 text-xs bg-slate-700/50 text-blue-300/80 px-3 py-1.5 rounded-full"
                          >
                            <CheckCircle className="w-3 h-3 text-cyan-400" />
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 mt-20 text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-cyan-500/30 rounded-3xl p-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              Essayez les nouvelles fonctionnalités
            </h2>
            <p className="text-blue-200/70 mb-8 max-w-xl mx-auto">
              Toutes ces fonctionnalités sont disponibles dès maintenant.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                <Upload className="w-5 h-5 mr-2" />
                Commencer maintenant
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

export default NewsPage;
